import { ApiResponse, hasFailed, SuccessfulResponse } from "@miragon/react-utils-api";
import {
    ActionCreatorWithoutPayload,
    ActionCreatorWithPayload,
    createSlice,
    Draft,
    PayloadAction
} from "@reduxjs/toolkit";
import { RootDispatch, RootState } from "../Store";
import {
    ApiState,
    ClearState,
    LoadFailed,
    LoadFailedPayload,
    LoadStarted,
    LoadSuccessfulPayload,
    reduceClearState,
    reduceLoadFailed,
    reduceLoadStarted
} from "./ApiState";

const CACHE_TIMEOUT = 3600;

export interface ListItem {
    id: string;
}

/**
 * The representation of a list redux store state. Useful if you load data indexed by an id from an
 * API and want to cache it. You can then trigger reloads or manual updates on it and still use the
 * current value.
 *
 * @template IdKey  The key of type that is used to index the map
 * @template Type   The type of the value contained in this state
 */
export interface ListApiState<Type extends ListItem> extends ApiState {
    /**
     * The actual value of the state. It consists of an id to value mapping and will be updated
     * (i.e. replaced) on each successful request. If a request fails, no changes to this field
     * will be made. If there has been no successful request yet, the value will be undefined.
     */
    readonly value: Type[] | undefined;
}

/**
 * The representation of the payload of a "load successful" action.
 *
 * @template Type The type of the actual result
 */
export interface ListLoadSuccessfulPayload<Type extends ListItem> extends LoadSuccessfulPayload {
    /**
     * The actual result of the request. Will be merged with the existing elements.
     */
    value: Type[];
}

/**
 * The representation of the payload of a manually triggered "update state" action.
 *
 * @template Type   The type of the actual result
 */
export interface ListUpdateStatePayload<Type extends ListItem> {
    /**
     * The entries to update.
     */
    update?: Type[];

    /**
     * The ids of the entries to delete.
     */
    delete?: ListItem[];

    /**
     * Whether to replace instead of update.
     */
    replace?: boolean;
}

/**
 * Constants used for creating the action names.
 */
const ListLoadSucceeded = "ListLoadSucceeded";
const ListUpdateState = "ListUpdateState";

/**
 * Create initial state for every list api state.
 *
 * @param name The globally unique name of this slice
 *
 * @template Type   The type of the actual result
 */
const createInitialListApiState = <Type extends ListItem>(
    name: string
): ListApiState<Type> => ({
    initialLoading: false,
    loading: false,
    name: name,
    value: undefined
});

/**
 * Processes a succeeded request. Sets the loading state to false, saves value, status code, load
 * time and resets the error.
 *
 * @param draft The current state
 * @param action The "load succeeded" action
 *
 * @template IdKey  The key of the type that is used to index the map
 * @template Type   The type of the actual result
 */
const reduceListLoadSucceeded = <Type extends ListItem>(
    draft: Draft<ListApiState<Type>>,
    action: PayloadAction<ListLoadSuccessfulPayload<Type>>
): void => {
    draft.loading = false;
    draft.initialLoading = false;
    draft.statusCode = action.payload.statusCode;
    draft.loadTime = new Date().getTime();
    draft.error = undefined;
    draft.value = action.payload.value as Draft<Type[]>;
};

/**
 * Processes an update state request. Updates or deletes the specified entries.
 *
 * @param draft The current state
 * @param action The "update state" action
 *
 * @template IdKey  The key of the type that is used to index the map
 * @template Type   The type of the actual result
 */
const reduceListUpdateState = <Type extends ListItem>(
    draft: Draft<ListApiState<Type>>,
    action: PayloadAction<ListUpdateStatePayload<Type>>
): void => {
    if (action.payload.replace) {
        draft.value = action.payload.update as Draft<Type[]>;
        return;
    } else {
        draft.value = draft.value
            ?.filter(entry => !action.payload.update?.find(u => u.id === entry.id))
            .filter(entry => !action.payload.delete?.find(d => d.id === entry.id))
            .concat((action.payload.update || []) as Draft<Type[]>);
    }
};

/**
 * Tries to find the list state of the slice with the specified name from the root state.
 *
 * @param state The root state
 * @param sliceName The name of the slice to find
 *
 * @template IdKey  The key of the type that is used to index the map
 * @template Type   The type of the actual result
 */
const getListSliceState = <Type extends ListItem>(
    state: RootState,
    sliceName: string
): ListApiState<Type> | undefined => (
    Object.values(state).find(slice => slice?.name === sliceName) as ListApiState<Type> | undefined
);

/**
 * The base options for each createListApiState request.
 *
 * @template Type   The type of the actual result
 */
type ListBaseOptions = {
    name: string
};

/**
 * The options for a createListApiState request that does not use a custom response.
 *
 * @template IdKey  The key of the type that is used to index the map
 * @template Type   The type of the actual result
 */
type ListDefaultOptions<Type extends ListItem> = ListBaseOptions & {
    execute: () => Promise<ApiResponse<Type[]>>
};

/**
 * The options for a createListApiState request that does use a custom response.
 *
 * @template IdKey      The key of the type that is used to index the map
 * @template Type       The type of the actual result
 * @template Response   The type of the api response
 */
type ListCustomOptions<Type extends ListItem, Response> = {
    execute: () => Promise<ApiResponse<Response>>,
    transform: (response: Response) => Type[]
} & ListBaseOptions;

/**
 * Used as options for the createListApiState method.
 */
type ListOptions<Type extends ListItem, Response> =
    | ListDefaultOptions<Type>
    | ListCustomOptions<Type, Response>;

/**
 * Type assertion to check if the options object uses a custom response transformator.
 *
 * @param param The options object to check
 */
const isCustomListResponse = <Type extends ListItem, Response>(
    param: ListOptions<Type, Response>
): param is ListCustomOptions<Type, Response> => (
    !!(param as ListCustomOptions<Type, Response>).transform
);

/**
 * Creates a new list api state for usage with redux toolkit. Returns the slice and the action to
 * start loading the content. All requests must return a list of entities, each containing an
 * unique id. The id is specified via the idKey parameter and will automatically be extracted,
 * unless you specify your own transform function.
 *
 * @template IdKey The type of the id in this state
 * @template Type The type of the value of the state
 * @template Response The type of the response of the api, defaults to Type[]
 *
 * @param options An options object with the following properties: {
 *     name:            The name of the state, must be unique
 *     execute:         A function to actually call the API. Should return either an array of Types
 *                      or an instance of Response
 *     transform:       A function to transform the api response manually instead of using the
 *                      default transform function. Required if execute returns an instance of
 *                      Response.
 * }
 *
 * @return The following items are contained in the returned array:
 *         slice: The state slice
 *         load: The action to trigger a load action
 *         updateState: The action to trigger a manual state update
 */
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
const createListApiState = <Type extends ListItem, Response = Type[]>(
    options: ListOptions<Type, Response>
) => {
    const loadStartedAction = options.name + LoadStarted;
    const loadFailedAction = options.name + LoadFailed;
    const loadSucceededAction = options.name + ListLoadSucceeded;
    const updateStateAction = options.name + ListUpdateState;

    const slice = createSlice({
        name: options.name,
        initialState: createInitialListApiState<Type>(options.name),
        reducers: {
            [loadStartedAction]: reduceLoadStarted,
            [loadFailedAction]: reduceLoadFailed,
            [loadSucceededAction]: reduceListLoadSucceeded,
            [updateStateAction]: reduceListUpdateState
        },
        extraReducers: builder => builder.addCase(ClearState, reduceClearState)
    });

    const loadStarted = slice.actions[loadStartedAction] as
        unknown as ActionCreatorWithoutPayload;

    const loadFailed = slice.actions[loadFailedAction] as
        ActionCreatorWithPayload<LoadFailedPayload>;

    const loadSucceeded = slice.actions[loadSucceededAction] as
        unknown as ActionCreatorWithPayload<ListLoadSuccessfulPayload<Type>>;

    const updateState = slice.actions[updateStateAction] as
        unknown as ActionCreatorWithPayload<ListUpdateStatePayload<Type>>;

    const load = (forceLoad = false) => async (
        dispatch: RootDispatch,
        getState: () => RootState
    ) => {
        const state = getListSliceState<Type>(getState(), options.name);

        if (!state) {
            throw new Error(`The list slice with name ${options.name} does not exist! Did you `
                + "you forget to add it to the root store?");
        }

        if (!forceLoad) {
            const loadTime = state.loadTime || 0;
            const maxAge = new Date().getTime() - CACHE_TIMEOUT * 1000;
            if (loadTime > maxAge) {
                return;
            }

            if (state.loading) {
                return;
            }
        }

        dispatch(loadStarted());
        const result = await options.execute();
        if (hasFailed(result)) {
            dispatch(loadFailed({
                error: result.error,
                statusCode: result.status
            }));
        } else {
            let transformed: Type[];
            if (isCustomListResponse(options)) {
                const typedResult = result as SuccessfulResponse<Response>;
                transformed = options.transform(typedResult.result);
            } else {
                const typedResult = result as SuccessfulResponse<Type[]>;
                transformed = typedResult.result;
            }
            dispatch(loadSucceeded({
                value: transformed,
                statusCode: result.status
            }));
        }
    };

    return [slice, load, updateState] as [typeof slice, typeof load, typeof updateState];
};

export default createListApiState;

// Solution to enable unit testing the state operations without exposing them to the public
export const testing = {
    createInitialListApiState,
    reduceListLoadSucceeded,
    reduceLoadStarted,
    reduceLoadFailed,
    reduceListUpdateState
};
